import { GetterTree, ActionTree, MutationTree } from "vuex";
import cs from "./constants";
import ZingTouch from "zingtouch";
import { throttle, debounce } from "throttle-debounce";
import MobileDetect from "mobile-detect";

let isTouchscreen = () => {
  return "ontouchstart" in window || navigator.msMaxTouchPoints > 0;
};

let isSmallDevice = () => {
  return window.screen.width < 1200;
};

interface IApplicationState {
  isMobile: boolean;
  isTouchscreen: boolean;
  scrollingByScreens: boolean;
  loadingScreenActive: boolean;
  loadingScreenHideTimestamp: number;
  loadingScreenAnimationEnded: boolean;
  moveDirection: string;
  planFocusedHouse: number;
  activeHouseInformationWindow: number;
  mapShowed: boolean;
  contentForSliderInWindow: { src: string; type: string }[];
  activeVideo: string;
  panoramasWindowOpened: boolean;
}

const state: IApplicationState = {
  isMobile: false,
  isTouchscreen: isTouchscreen(),
  scrollingByScreens: !isSmallDevice(),
  loadingScreenActive: true,
  loadingScreenHideTimestamp: 0,
  loadingScreenAnimationEnded: false,
  moveDirection: "",
  planFocusedHouse: 1,
  activeHouseInformationWindow: 0,
  mapShowed: false,
  contentForSliderInWindow: [],
  activeVideo: "",
  panoramasWindowOpened: false
};

const getters: GetterTree<IApplicationState, any> = {
  pageBehindAWindow: (state: IApplicationState) => {
    return (
      state.contentForSliderInWindow.length > 0 ||
      state.activeHouseInformationWindow > 0
    );
  }
};

const actions: ActionTree<IApplicationState, any> = {
  [cs.deviceDetection]({ commit, dispatch, state }) {
    commit(cs.deviceDetection);
  },

  /********************************************* */

  [cs.loadingScreenHide]({ commit, dispatch, state }) {
    commit(cs.loadingScreenHide);
  },

  /********************************************* */

  [cs.backgroundImagesLoaded]({ commit, dispatch, state }) {
    commit(cs.backgroundImagesLoaded);
  },

  /********************************************* */

  [cs.loadingScreenAnimationEnded]({ commit, dispatch, state }) {
    commit(cs.loadingScreenAnimationEnded);
  },

  /********************************************* */

  [cs.changeMoveDirection]({ commit, dispatch, state }, direction: string) {
    commit(cs.changeMoveDirection, direction);
  },

  /********************************************* */

  [cs.changeActiveHouseInformationWindow](
    { commit, dispatch, state },
    windowNumber: number
  ) {
    commit(cs.changeActiveHouseInformationWindow, windowNumber);
  },

  /********************************************* */

  [cs.windowWithSliderContentChange]({ commit, dispatch, state }, items: any) {
    commit(cs.windowWithSliderContentChange, items);
  },

  /********************************************* */

  [cs.showVideo]({ commit, dispatch, state }, videoLink: string) {
    commit(cs.showVideo, videoLink);
  },

  /********************************************* */

  [cs.showPanoramas]({ commit, dispatch, state }) {
    commit(cs.showPanoramas);
  },

  /********************************************* */

  [cs.hidePanoramas]({ commit, dispatch, state }) {
    commit(cs.hidePanoramas);
  }
};

const mutations: MutationTree<IApplicationState> = {
  [cs.loadingScreenHide](state) {
    state.loadingScreenActive = false;
    state.loadingScreenHideTimestamp = new Date().getTime();
  },

  /********************************************* */

  [cs.loadingScreenAnimationEnded](state) {
    state.loadingScreenAnimationEnded = true;
  },

  /********************************************* */

  [cs.deviceDetection](state, detect: MobileDetect) {
    var md = new MobileDetect(window.navigator.userAgent);
    state.isMobile = typeof md.mobile() === "string";
  },

  /********************************************* */

  [cs.changeMoveDirection](state, direction: string) {
    state.moveDirection = direction;
  },

  /********************************************* */

  [cs.changeActiveHouseInformationWindow](state, windowNumber: number) {
    state.activeHouseInformationWindow = windowNumber;
  },

  /********************************************* */

  [cs.windowWithSliderContentChange](state, items: any) {
    state.contentForSliderInWindow = items;
  },

  /********************************************* */

  [cs.showVideo](state, video: string) {
    state.activeVideo = video;
  },

  /********************************************* */

  [cs.showPanoramas](state) {
    state.panoramasWindowOpened = true;
  },

  /********************************************* */

  [cs.hidePanoramas](state) {
    state.panoramasWindowOpened = false;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
